// @flow
import React, { ReactElement, ChangeEventHandler, StyleHTMLAttributes } from "react";
import type {
  UiSchema,
  RJSFSchema,
  FormContextType,
  StrictRJSFSchema,
  FieldProps,
  FieldTemplateProps,
  Registry,
} from "@rjsf/utils";
import { localize } from "redux-i18n";
import classNames from "classnames";
import { LabelTemplate } from "@components/schema-form/templates";
import { View, Text, Button } from "@components";

export type Props = {
  ...FieldProps,
};
/**
 *
 * @param props
 * @returns {JSX.Element|Node|*|React.Node}
 * @constructor
 *
 *  You can use ui:options.labelposition === "left", "right OR "vertical"
 *  in the uiSchema to position the label next to the field instead of top
 *
 */

const FieldTemplate = (props: Props) => {
  const { uiSchema } = props;
  const uiOptions = uiSchema?.["ui:options"] || {};
  const uiWidget = uiSchema?.["ui:widget"];
  const uiField = uiSchema?.["ui:field"];
  const uiHeader = uiSchema?.["ui:header"];

  if (props.schema.type === "object") {
    return props.children;
  }

  if (props.schema.type === "array" && uiField !== "multi-select") {
    // The Multi-select will be rendered as any other regular field
    return props.children;
  }
  const errors = props.errors;
  const label = uiOptions && uiOptions.label;
  const labelposition: "left" | "top" = uiOptions.labelposition;
  const hidelabel = !!uiOptions.hidelabel;
  const labelclass = uiOptions.labelclass || "";
  const isDatePicker = uiWidget === "date-picker";
  const className = uiOptions.className;
  const isRemovable = uiOptions.removable === true;

  const columnsCnt = uiOptions.columns;

  const gridColsAndRows = uiOptions && {
    rowStart: uiOptions.rowStart,
    colStart: uiOptions.colStart,
    rowEnd: uiOptions.rowEnd,
    colEnd: uiOptions.colEnd,
  };

  // It expects value like the CSS prop aspect-ratio "1/3" or "1/16" $FlowIgnore
  const layoutFlexRatio = uiSchema["ui:layoutFlexRatio"]?.split("/");
  const labelFlex = (layoutFlexRatio && layoutFlexRatio[0]) || 1;
  const fieldFlex = (layoutFlexRatio && layoutFlexRatio[1]) || 3;
  const description = uiOptions && uiOptions.description;
  const rowBackground = uiOptions && uiOptions.rowBackground;
  const isBoxGrid = uiSchema.boxGrid;
  const headerOptions = uiOptions.header || {};

  return (
    <View
      display={uiWidget == "hidden" && "none"}
      id={`${props.id}-field-template`}
      name="field-template"
      gridTemplateColumns={columnsCnt}
      {...gridColsAndRows}
    >
      {uiHeader && (
        <View
          separator="horizontal grey"
          backgroundColor={headerOptions.backgroundColor || "light-grey"}
          padding="4px"
          margin="12px 0 8px 0"
        >
          <Text type={headerOptions.type || "h6"} color="not-so-black">
            {uiHeader}
          </Text>
        </View>
      )}
      {isBoxGrid && (
        <View
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          className={classNames("border-1px-charcoal-light", className)}
          padding="0.5rem"
          minHeight="48px"
          backgroundColor={rowBackground}
          gap={2}
        >
          {!hidelabel && (
            <View flex={2} direction="row" justifyContent="space-between" alignItems="center">
              <View>
                <LabelTemplate {...props} className="text-color-black font-weight-bold" />
                {description ? <Text>{description}</Text> : null}
              </View>
              {props.required && !props.readonly && (
                <View width={6} alignItems="center" className="text-warning font-size-20">
                  *
                </View>
              )}
            </View>
          )}

          <View flex={1} justifyContent="center">
            {/* TODO: check if removing  className={props.rawErrors && "has-error"} will cause any issues - if so, uncoment the following line */}
            <View className="box-grid">{props.children}</View>
            {props.help}
            {props.errors}
            {isRemovable && (
              <Button
                className="secondary delete-item-button"
                onClick={props.children.onDropIndexClick(props.children.id)}
                materialIcon="delete_forever"
                disabled={props.disabled}
              />
            )}
          </View>
        </View>
      )}

      {!isBoxGrid && (
        <View
          justifyContent={"space-between"}
          minHeight={10}
          direction={labelposition === "top" ? "column" : "row"}
        >
          {!hidelabel && (
            <View
              flex={labelFlex}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <LabelTemplate className={classNames(labelclass)} {...props} />
              {props.required && !props.readonly && (
                <View width={6} alignItems="center" className="text-warning font-size-20">
                  *
                </View>
              )}
            </View>
          )}

          <View flex={fieldFlex} justifyContent="center">
            {/* TODO: check if removing  className={props.rawErrors && "has-error"} will cause any issues - if so, uncoment the following line */}
            <View className={props.rawErrors && "has-error"}>{props.children}</View>
            {/* <View>{props.children}</View> */}
            {props.help}
            {props.errors}
            {isRemovable && (
              <Button
                className="secondary delete-item-button"
                onClick={props.children.onDropIndexClick(props.children.id)}
                materialIcon="delete_forever"
                disabled={props.disabled}
              />
            )}
          </View>
        </View>
      )}
    </View>
  );
};

export default localize()(FieldTemplate);
